import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { clearToken, getToken, login, me } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

const destinationFor = (user, requested) => {
  const adminDestinations = ['/admin/scholars', '/admin/inventory', '/admin/orders', '/admin/season', '/admin/announcements', '/admin/products', '/admin/contributors', '/admin/alumni-gallery', '/admin/champions', '/admin/coaches'];
  const eventDestinations = ['/admin/events', '/admin/football-practice-times'];
  if (user.roles?.includes('admin') && adminDestinations.includes(requested)) return requested;
  if (user.roles?.includes('event_editor') && eventDestinations.includes(requested)) return requested;
  if ((user.roles?.includes('roster_editor') || user.roles?.includes('coach')) && requested === '/roster') return requested;
  if (user.roles?.includes('roster_editor')) return '/roster';
  if (user.roles?.includes('coach')) return '/roster';
  if (user.roles?.includes('event_editor')) return '/admin/events';
  return '/admin/scholars';
};

export default function AdminLogin() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const requestedNext = searchParams.get('next');

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    me().then((user) => nav(destinationFor(user, requestedNext), { replace: true })).catch(() => { clearToken(); setChecking(false); });
  }, [nav, requestedNext]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast({ title: 'Welcome back!', description: 'Logged in successfully.' });
      nav(destinationFor(user, requestedNext));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast({ title: 'Login failed', description: typeof detail === 'string' ? detail : 'Try again.' });
    } finally {
      setBusy(false);
    }
  };

  if (checking) return <div className="min-h-screen bg-[#0a1f44]" data-testid="admin-login-checking" />;

  return (
    <div className="min-h-[100dvh] bg-[#0a1f44] flex items-center justify-center p-4 sm:p-6 font-body">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={iceLogo} alt="ICE" className="h-16 w-auto object-contain mx-auto mb-4" />
          <h1 className="font-display text-white text-4xl" data-testid="admin-login-heading">{requestedNext === '/roster' ? 'COACH LOGIN' : 'ADMIN LOGIN'}</h1>
          <p className="text-white/60 text-sm mt-2 font-heading tracking-widest uppercase text-xs" data-testid="admin-login-subheading">{requestedNext === '/roster' ? 'Secure roster access' : '"ICE" Brookline JP Patriots'}</p>
        </div>
        <form onSubmit={submit} className="bg-white p-5 sm:p-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-email"
              autoComplete="username"
              inputMode="email"
              className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
              placeholder="admin@brooklinejppatriots.com"
            />
          </div>
          <div>
            <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password"
              autoComplete="current-password"
              className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-btn"
            className="w-full bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest px-8 py-3 inline-flex items-center justify-center gap-2 transition-colors"
          >
            <Lock className="w-4 h-4" /> {busy ? 'Signing in…' : 'Sign In'} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
